import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  CircularProgress,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import { FavoriteBorder } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../context/FavoritesContext';
import { useDiscoveryViewMode } from '../hooks/useDiscoveryViewMode';
import FavoriteButton from '../components/FavoriteButton';
import CaretakerFaceTile from '../components/CaretakerFaceTile';
import DiscoveryViewToggle from '../components/DiscoveryViewToggle';
import { FavoriteCaregiver } from '../services/favoritesService';
import { getCaretakerAvatarUrl, getCaretakerDisplayName } from '../utils/caretakerDisplay';

// FEAT-057 — Save / Favorite Caregivers (MVP-18.29).
// FEAT-085 — Faces/Cards toggle on the saved caregivers grid.

const FACE_GRID_BREAKPOINTS = { xs: 4, sm: 3, md: 2, lg: 2 } as const;
const CARD_GRID_BREAKPOINTS = { xs: 12, sm: 6, md: 4 } as const;

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const { favorites, loading, refresh } = useFavorites();
  const { viewMode, setViewMode } = useDiscoveryViewMode();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const renderFavoriteCard = (favorite: FavoriteCaregiver) => {
    const displayName = getCaretakerDisplayName(favorite.caregiver);

    return (
      <Card
        sx={{ position: 'relative', height: '100%' }}
        data-testid={`favorite-card-${favorite.caregiverId}`}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <FavoriteButton
            caregiverId={favorite.caregiverId}
            size="small"
            summary={favorite.caregiver}
          />
        </Box>
        <CardActionArea
          onClick={() => navigate(`/caretaker/${favorite.caregiverId}`)}
          sx={{ height: '100%' }}
        >
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Avatar
                src={getCaretakerAvatarUrl(favorite.caregiver, displayName)}
                alt={displayName}
                sx={{ width: 72, height: 72, mb: 1.5, border: '3px solid', borderColor: 'primary.main' }}
              />
              <Typography variant="h6" component="h3" gutterBottom>
                {displayName}
              </Typography>
              {favorite.caregiver.location && (
                <Chip label={favorite.caregiver.location} size="small" variant="outlined" sx={{ mb: 1 }} />
              )}
              {favorite.caregiver.bio && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {favorite.caregiver.bio}
                </Typography>
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="favorites-page">
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} gap={2}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Saved Caregivers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Caregivers you&apos;ve saved. They stay here across sessions until you remove them.
          </Typography>
        </Box>
        {favorites.length > 0 && (
          <DiscoveryViewToggle viewMode={viewMode} onChange={setViewMode} />
        )}
      </Box>

      {loading && favorites.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
        </Box>
      ) : favorites.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }} data-testid="favorites-empty">
          <FavoriteBorder sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            No saved caregivers yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Tap the heart on any caregiver to save them here for later.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/search-caretakers')}>
            Find Caregivers
          </Button>
        </Paper>
      ) : (
        <Grid
          container
          spacing={viewMode === 'faces' ? 2 : 3}
          data-testid={
            viewMode === 'faces' ? 'favorites-results-faces' : 'favorites-results-cards'
          }
        >
          {favorites.map((favorite) => (
            <Grid
              item
              {...(viewMode === 'faces' ? FACE_GRID_BREAKPOINTS : CARD_GRID_BREAKPOINTS)}
              key={favorite.caregiverId}
            >
              {viewMode === 'faces' ? (
                <CaretakerFaceTile
                  caretaker={favorite.caregiver}
                  onPress={() => navigate(`/caretaker/${favorite.caregiverId}`)}
                  testId={`favorite-face-${favorite.caregiverId}`}
                />
              ) : (
                renderFavoriteCard(favorite)
              )}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default FavoritesPage;
